import type { CarAkte } from '@ahd/shared/types/ahd-cars/car/akte';
import type { Car } from '@ahd/shared/types/ahd-cars/car/car';
import type { CarImage } from '@ahd/shared/types/ahd-cars/car/image';
import type { CarServiceSaveBhData, CarServiceSaveData } from '@ahd/shared/types/ahd-cars/car/save';
import type { EyecatcherEditJSON, FirmaBilderSettings } from '@ahd/shared/types/ahd-cars/firma/bilder-settings';
import type { Firma } from '@ahd/shared/types/ahd-cars/firma/firma';
import type { Lead } from '@ahd/shared/types/ahd-cars/lead/lead';
import type { AxiosRequestConfig } from 'axios';
import { type ErrorLevels } from '../_json/s_errorLevel';
import { type UserSettings } from '../login/EnsureLoggedInContainer';
import { LEAD } from '../vars';
import axios from './Axios';
import { type Session, getFirma, getIdFirma, getIdUser, getSessionHeader, getToken, isDebug } from './Session';
import { stringToSlug } from './Texte';
import { type NetworkProgress, checkUndefined, checkUndefinedNullOrEmpty, checkUndefinedOrNull, isLocalServer, networkHandler } from './Tools';
import type { UploadConfig } from './inputfields/Image';

export const GBASE_DOMAIN = 'cars.autohaus-digital.de';
const GBASE_URL = isLocalServer() ? `https://${GBASE_DOMAIN}` : location.origin;

export const urlRelease = GBASE_URL;
export const urlDataRelease = GBASE_URL + '/r_data';
export const urlDocRelease = GBASE_URL + '/r_doc';
export const urlExportRelease = GBASE_URL + '/r_export';
export const urlImportRelease = GBASE_URL + '/r_import';
export const urlImagesRelease = GBASE_URL + '/r_images';
export const urlSRelease = GBASE_URL + '/s';
export const urlServicesRelease = GBASE_URL + '/r_services';
export const urlSupportRelease = GBASE_URL + '/r_support';

export const urlAhdWebRelease = 'https://web-servlets.autohaus-digital.de';

export function testService(statuscode, method = 'get', data = {}) {
    const url = 'https://httpstat.us/' + statuscode;
    return axios({
        title: 'Testen des Errorloggings',
        noLogging: true,
        method: method,
        url: url,
        data: data,
    });
}

export function log(idFirma: number, subUser: number, level: ErrorLevels, error: string) {
    const url = urlRelease + '/error/v2/errorCD';
    return axios({
        method: 'post',
        url: url,
        data: {
            user: [idFirma, subUser].join('.'),
            err: error,
        },
        noLogging: true,
        title: 'Loggen eines Fehlers',
    });

    /* const url = `${urlDataRelease}/log/v1/addCD/${idFirma}/${subUser}/${formatTimestamp(new Date())}/${level}`;
    return axios({
        method: 'post',
        url: url,
        data: error,
        noLogging: true,
        noToast: true,
        title: 'Loggen eines Fehlers',
    }); */
}

export function getAxiosHeader(customToken?: string, customTokenHeaderName?: string) {
    return { [customTokenHeaderName || getSessionHeader() || 'authentication']: 'Bearer ' + (customToken || getToken()) };
}

export function getAxiosHeaderForce() {
    const sessionHeader = getSessionHeader();
    if (!checkUndefinedNullOrEmpty(sessionHeader)) {
        return { [sessionHeader]: 'Bearer ' + getToken(), 'If-Modified-Since': '1' };
    }
}

export function getAxiosHeaderForm() {
    const sessionHeader = getSessionHeader();
    if (!checkUndefinedNullOrEmpty(sessionHeader)) {
        return { [sessionHeader]: 'Bearer ' + getToken(), 'Content-Type': 'application/x-www-form-urlencoded' };
    }
}

export function getAxiosHeaderJPG() {
    const sessionHeader = getSessionHeader();
    if (!checkUndefinedNullOrEmpty(sessionHeader)) {
        return { [sessionHeader]: 'Bearer ' + getToken(), 'Content-Type': 'image/jpg' };
    }
}

export function getAxiosHeaderPDF() {
    const sessionHeader = getSessionHeader();
    if (!checkUndefinedNullOrEmpty(sessionHeader)) {
        return { [sessionHeader]: 'Bearer ' + getToken(), 'Content-Type': 'application/pdf' };
    }
}

export function getAxiosHeaderZIP() {
    const sessionHeader = getSessionHeader();
    if (!checkUndefinedNullOrEmpty(sessionHeader)) {
        return { [sessionHeader]: 'Bearer ' + getToken(), 'Content-Type': 'application/zip' };
    }
}

export function getAxiosHeaderXML() {
    const sessionHeader = getSessionHeader();
    if (!checkUndefinedNullOrEmpty(sessionHeader)) {
        return { [sessionHeader]: 'Bearer ' + getToken(), 'Content-Type': 'application/xml' };
    }
}

export function getAxiosHeaderText() {
    const sessionHeader = getSessionHeader();
    if (!checkUndefinedNullOrEmpty(sessionHeader)) {
        return { [sessionHeader]: 'Bearer ' + getToken(), 'Content-Type': 'text/plain' };
    }
}

export function getAxiosConfigZIP() {
    const sessionHeader = getSessionHeader();
    if (!checkUndefinedNullOrEmpty(sessionHeader)) {
        return { headers: { [sessionHeader]: 'Bearer ' + getToken(), 'Content-Type': 'application/zip' } };
    }
}

export function getAxiosConfigPDF() {
    const sessionHeader = getSessionHeader();
    if (!checkUndefinedNullOrEmpty(sessionHeader)) {
        return { headers: { [sessionHeader]: 'Bearer ' + getToken(), 'Content-Type': 'application/pdf' } };
    }
}

export function getAxiosConfigTextPlain() {
    const sessionHeader = getSessionHeader();
    if (!checkUndefinedNullOrEmpty(sessionHeader)) {
        return { headers: { [sessionHeader]: 'Bearer ' + getToken(), 'Content-Type': 'text/plain' } };
    }
}

export function getAxiosConfigForm() {
    const sessionHeader = getSessionHeader();
    if (!checkUndefinedNullOrEmpty(sessionHeader)) {
        return { headers: { [sessionHeader]: 'Bearer ' + getToken(), 'Content-Type': 'application/x-www-form-urlencoded' } };
    }
}

export function getAxiosConfig() {
    return { headers: getAxiosHeader() };
}

export function uploadFile(formData, config) {
    const url = urlSRelease + '/files/upload/' + getIdFirma() + '/' + getIdUser();

    return axios({
        title: 'Hochladen der Datei',
        method: 'post',
        url: url,
        data: formData,
        ...config,
        headers: getAxiosHeaderForm(),
    });
}

export function setUserSettings(data: UserSettings, force = false) {
    if (force || (!force && !isDebug())) {
        const url = urlSRelease + '/v1/settings/user/cd';
        return axios({
            title: 'Speichern der Benutzereinstellungen',
            method: 'post',
            url: url,
            headers: getAxiosHeader(),
            data: data,
        });
    } else {
        return Promise.resolve();
    }
}

export function getStandzeitenDiagramm(param) {
    let url = urlServicesRelease + '/cars/v1/stat/standzeit';
    if (param) url += '?' + param.join('&');
    return axios({
        title: 'Abrufen des Standzeitdiagramms',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getPreisstatistik(param, dateFrom, dateBis) {
    let url = urlServicesRelease + '/cars/v1/history/abpreisungen/' + dateFrom + '/' + dateBis;
    if (param) url += '?' + param.join('&');
    return axios({
        title: 'Abrufen der Preisstatistik',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getStandzeitenEinsDiagramm(tage, param) {
    let url = urlServicesRelease + '/cars/v5/stat/standzeit/' + tage;
    if (param) url += '?' + param.join('&');

    return axios({
        title: 'Abrufen des Standzeit-Eins-Diagramms',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function addB2BFirma(idFirma, data) {
    const url = urlSRelease + '/firma/saveB2B/firma/' + idFirma + '/user/' + getIdUser();
    return axios({
        title: 'Hinzufügen der B2B-Firma',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function saveAktRolle(aktRolle) {
    const url = urlSRelease + '/firma/aktRole/' + aktRolle;
    return axios({
        title: 'Speichern der aktuellen Rolle',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function saveAttr(idCar: number, data: string) {
    const params = new URLSearchParams();
    params.append('changes', data);
    const url = urlServicesRelease + '/cars/v4/uweweb/car/' + idCar + '/attr/add';
    return axios({
        title: 'Speichern der Ausstattung',
        method: 'post',
        url: url,
        data: params,
        headers: getAxiosHeader(),
    });
}

export function ignoreAttr(idCar: number, data: string) {
    const url = urlServicesRelease + '/cars/v1/statAS24/removeAttr/' + idCar;
    return axios({
        title: 'Ignorieren der Ausstattung',
        method: 'post',
        url: url,
        data,
        headers: getAxiosHeader(),
    });
}

export function removeAttr(idCar, data) {
    const params = new URLSearchParams();
    params.append('changes', data);
    const url = urlServicesRelease + '/cars/v4/uweweb/car/' + idCar + '/attr/remove';
    return axios({
        title: 'Entfernen der Ausstattung',
        method: 'post',
        url: url,
        data: params,
        headers: getAxiosHeader(),
    });
}

export function zurueckbuchen(idFirma, idCar) {
    const url = urlServicesRelease + '/cars/v4/uweweb/firma/' + idFirma + '/car/' + idCar + '/zurueckbuchen';
    return axios({
        title: 'Zurückbuchen',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function zurueckbuchenBulk(cars) {
    const url = urlServicesRelease + '/cars/v4/uweweb/firma/' + getIdFirma() + '/zurueckbuchen';
    return axios({
        title: 'Zurückbuchen',
        method: 'post',
        url: url,
        data: cars,
        headers: getAxiosHeader(),
    });
}

export function deleteCar(idFirma, idCar) {
    const url = urlServicesRelease + '/cars/v4/uweweb/firma/' + idFirma + '/car/' + idCar + '/del';
    return axios({
        title: 'Löschen des Fahrzeugs',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function deleteCars(idFirma, cars) {
    const url = urlServicesRelease + '/cars/v4/uweweb/firma/' + idFirma + '/del';
    return axios({
        title: 'Löschen der Fahrzeuge',
        method: 'post',
        url: url,
        data: cars,
        headers: getAxiosHeader(),
    });
}

export function saveReservation(data, idCar) {
    const url = urlServicesRelease + '/cars/v4/reservierung/' + idCar;
    return axios({
        title: 'Speichern der Reservierung',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function getSaveIdKraftstoff(data) {
    const url = urlServicesRelease + '/cars/v4/label/';
    return axios({
        title: 'Abrufen der Kraftstoff-ID',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function addSchaden(idCar, data) {
    let sub = 0;
    const firma = getFirma();
    for (let i = 0; i < firma.usr.length; i++) {
        if (getIdUser() == firma.usr[i].i) {
            sub = firma.usr[i].s;
        }
    }
    const url = urlServicesRelease + '/cars/v2/uweweb/saveDamage/firma/' + getIdFirma() + '/user/' + sub + '/car/' + idCar;
    return axios({
        title: 'Hinzufügen des Schadens',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function deleteSchaden(idCar, idDamage) {
    const url = urlServicesRelease + '/cars/v2/uweweb/delDamage/car/' + idCar + '/' + idDamage;
    return axios({
        title: 'Entfernen des Schadens',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getServicesCars(idCar: Car['id'], config?: AxiosRequestConfig) {
    const url = urlServicesRelease + '/cars/v1/vehicle/' + getIdFirma() + '/' + getIdUser() + '/' + idCar;
    return axios<Car>({
        title: 'Abrufen der Fahrzeugdaten',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
        ...config,
    });
}

export function getCarByBarcode(strBarcode) {
    const url = urlServicesRelease + '/cars/v1/qr/car';
    return axios({
        title: 'Abrufen des Barcodes',
        method: 'post',
        url: url,
        data: 'barcode=' + strBarcode,
        headers: getAxiosHeader(),
    });
}

export function getAbteilungen() {
    const url = urlServicesRelease + '/firma3/api/abteilung';
    return axios<{ list: { i: number; t: string }[] }>({
        title: 'Abrufen der Abteilungen',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getHersteller(kat) {
    const url = urlServicesRelease + '/aus/api/hersteller' + (kat ? '/' + kat : '');
    return axios({
        title: 'Abrufen der Hersteller',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getModelle(hs) {
    const url = urlServicesRelease + '/aus/api/mod/' + hs;
    return axios({
        title: 'Abrufen der Modelle',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getVehicleTyp() {
    const url = urlDataRelease + '/dat2017/v2/modell';
    return axios({
        title: 'Abrufen der Typen',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getVehicleHersteller(gruppe) {
    const url = urlDataRelease + '/dat2017/v2/modell/' + gruppe;
    return axios({
        title: 'Abrufen der Hersteller',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getVehicleModell(gruppe, hersteller) {
    const url = urlDataRelease + '/dat2017/v2/modell/' + gruppe + '/' + hersteller;
    return axios({
        title: 'Abrufen der Modelle',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getVehicleSubModell(gruppe, hersteller, modell) {
    const url = urlDataRelease + '/dat2017/v2/modell/' + gruppe + '/' + hersteller + '/' + modell;
    return axios({
        title: 'Abrufen der Submodelle',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getVehicleVariante(gruppe, hersteller, modell, idSubModell) {
    const url = urlDataRelease + '/dat2017/v2/modell/' + gruppe + '/' + hersteller + '/' + modell + '/' + idSubModell;
    return axios({
        title: 'Abrufen der Varianten',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getVehiclesLead(cars, signal: AbortController['signal']) {
    const url = urlSRelease + '/v1/cars/data';
    return axios({
        title: 'Abrufen der Fahrzeug-Leads',
        method: 'post',
        url: url,
        data: cars,
        headers: getAxiosHeader(),
        signal: signal,
    });
}

export function getVehicleLead(idCar) {
    const url = urlSRelease + '/lead/car/data/' + getIdFirma() + '/' + getIdUser() + '/' + idCar;
    return axios<{ leadsOpen: Lead[]; leadsClosed: Lead[] }>({
        title: 'Abrufen der Fahrzeug-Leads',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getServicesSoldCars(idCar: Car['id'], config?: AxiosRequestConfig) {
    const url = urlServicesRelease + '/cars/v1/vehicle/sold/' + getIdFirma() + '/' + idCar;
    return axios({
        title: 'Abrufen der Fahrzeug-Daten',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
        ...config,
    });
}

export function getServicesProvision(idCar) {
    const url = urlServicesRelease + '/cars/v4/provision/' + idCar;
    return axios({
        title: 'Abrufen der Provision',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function changePortalFeature(idCar: Car['id'], data: string) {
    return axios({
        title: 'Deaktivieren des Verstärkers',
        method: 'post',
        url: `${urlServicesRelease}/cars/v1/vehicle/portalFeature/${getIdFirma()}/${idCar}`,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function checkChassisGroup(idFirma, vin) {
    const url = urlServicesRelease + '/cars/v1/status/statusChassisGroup/' + idFirma + '/' + vin;
    return axios({
        title: 'Prüfen der gelöschten Fahrzeuge',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function postImport(adresse, data) {
    const url = urlServicesRelease + '/cars/' + adresse;
    return axios({
        title: 'Anlegen des Fahrzeugs',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function postCarUpdate(adresse, idCar, data) {
    const url = urlServicesRelease + '/cars/' + adresse + '/' + idCar;
    return axios({
        title: 'Aktualisieren der Fahrzeugdaten',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function postSollpreisInzahlungsnahme(idCar, strData) {
    let sub = 0;
    const firma = getFirma();
    for (let i = 0; i < firma.usr.length; i++) {
        if (getIdUser() == firma.usr[i].i) {
            sub = firma.usr[i].s;
        }
    }
    const url = urlServicesRelease + '/cars/v2/uweweb/firma/' + getIdFirma() + '/user/' + sub + '/car/' + idCar;
    return axios({
        title: 'Aktualisieren der Fahrzeugdaten',
        method: 'post',
        url: url,
        data: strData,
        headers: getAxiosHeader(),
    });
}

export function getRechnungsnummer(idCar) {
    const url = urlServicesRelease + '/cars/v4/vehicleRechnung/' + idCar + '/generateRechnungsNummer';
    return axios({
        title: 'Abrufen der Rechnungsnummer',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function sellCar(idFirmaCar, idCar, strData, strDataRechnung, strDK, strVB, strBH, strDataSold, strProv) {
    const params = new URLSearchParams();
    params.append('changes', strData);
    params.append('rechnung', strDataRechnung);
    params.append('dk', strDK);
    params.append('vb', strVB);
    params.append('bh', strBH);
    params.append('ads', strDataSold);
    params.append('prov', strProv);
    const url = urlServicesRelease + '/cars/v4/uweweb/firma/' + idFirmaCar + '/car/' + idCar + '/sell';
    return axios({
        title: 'Verkaufen des Fahrzeugs',
        method: 'post',
        url: url,
        data: params,
        headers: getAxiosHeader(),
    });
}

export function updateCar(idCar, data: CarServiceSaveData, bh: CarServiceSaveBhData) {
    const strData = !checkUndefinedOrNull(data) ? JSON.stringify(data) : '{}';
    const strBH = !checkUndefinedOrNull(bh) ? JSON.stringify(bh) : '{}';

    const params = new URLSearchParams();
    params.append('changes', strData);
    params.append('bh', strBH);
    const url = urlServicesRelease + '/cars/v4/uweweb/car/' + idCar;
    return axios({
        title: 'Aktualisieren der Fahrzeugdaten',
        method: 'post',
        url: url,
        data: params,
        headers: getAxiosHeader(),
    });
}

export function saveZahlungsplan(idCar, data) {
    const url = urlServicesRelease + '/cars/v4/vehicleZahlung/' + idCar;
    return axios({
        title: 'Speichern des Zahlungsplans',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function buildImageURL(idFirma: number, idImage: number, w: number, ts?: number) {
    return `${urlImagesRelease}/s/v1/imgc/${idFirma}/${idImage}/${w}${!checkUndefinedNullOrEmpty(ts) ? `?ts=${ts}` : ''}`;
}

export function getServicesImages(idCar: number, idHersteller = 0, idSiegel = 0, idStandort?: number) {
    let url;

    if (!idHersteller) url = urlImagesRelease + '/s/v2/firma/' + getIdFirma() + '/car/' + idCar + '/' + (idStandort || getIdFirma()) + '/all';
    else url = urlImagesRelease + '/s/v2/firma/' + getIdFirma() + '/car/' + idCar + '/' + (idStandort || getIdFirma()) + '/0/' + idHersteller + '/' + idSiegel + '/all';

    return axios<CarImage[]>({
        title: 'Laden der Fahrzeugbilder',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getImagesOrigZip(idCar, downloadProgressFunc: (progressEvent: NetworkProgress) => void, signal: AbortController['signal']) {
    const url = urlImagesRelease + '/s/v2/firma/' + getIdFirma() + '/' + idCar + '/2/nocl_zip';

    return axios({
        title: 'Herunterladen der Fahrzeugbilder',
        method: 'get',
        url: url,
        responseType: 'blob',
        headers: { ...getAxiosHeader(), 'Content-Type': 'application/zip' },
        onDownloadProgress: (progressEvent) => networkHandler(progressEvent, downloadProgressFunc),
        signal: signal,
        timeout: 0,
    });
}

export function getFileByUrl(url, downloadProgressFunc: (progressEvent: NetworkProgress) => void, signal: AbortController['signal']) {
    return axios({
        title: 'Herunterladen der Datei',
        method: 'get',
        url: url,
        responseType: 'blob',
        headers: getAxiosHeader(),
        onDownloadProgress: (progressEvent) => networkHandler(progressEvent, downloadProgressFunc),
        signal: signal,
        timeout: 0,
    });
}

export function putFirmenbild(typ, file, config, idFirma) {
    const url = urlImagesRelease + '/s/v1/firma/' + idFirma + typ;
    return axios({
        title: 'Hochladen des Firmenbildes',
        method: 'put',
        url: url,
        data: file,
        ...config,
        headers: { ...config.headers, ...getAxiosHeader() },
    });
}

export function putFirmenbild2(typ, file, config, idFirma) {
    const url = urlImagesRelease + '/s/v2/firma/' + idFirma + typ;
    return axios({
        title: 'Hochladen des Firmenbildes',
        method: 'put',
        url: url,
        data: file,
        ...config,
        headers: { ...config.headers, ...getAxiosHeader() },
    });
}

export function deleteFirmenbild(idBild) {
    const url = urlImagesRelease + '/s/v3/firma/' + idBild;
    return axios({
        title: 'Löschen des Firmenbildes',
        method: 'delete',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getDefaultEcJson() {
    const url = urlImagesRelease + '/s/v1/firma/getDefaultFDEyecatcherConfig';
    return axios({
        title: 'Abrufen der Standard-JSON',
        method: 'get',
        url: url,
        headers: { ...getAxiosHeader() },
    });
}

export function checkEcJsonData(idFirma: number, idEC: number, data: EyecatcherEditJSON) {
    return axios({
        title: 'Prüfen der JSON',
        method: 'post',
        url: `${urlImagesRelease}/s/v1/firma/checkEyecatcherConfig/${idFirma}/${idEC}`,
        data,
        headers: { ...getAxiosHeader() },
    });
}

export function saveEcJsonData(idFirma: number, idEC: number, data: EyecatcherEditJSON) {
    return axios({
        title: 'Speichern der JSON',
        method: 'post',
        url: `${urlImagesRelease}/s/v1/firma/updateEyecatcherConfig/${idFirma}/${idEC}`,
        data,
        headers: { ...getAxiosHeader() },
    });
}

export function setECTyp(idFirma, ectyp) {
    const url = urlImagesRelease + '/s/v2/firma/' + idFirma + '/setEyecatcher/' + ectyp;
    return axios({
        title: 'Setzen des Eyecatcher-Typ',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function disableEC(idFirma) {
    const url = urlImagesRelease + '/s/v2/firma/' + idFirma + '/disableEyecatcher';
    return axios({
        title: 'Löschen des Eyecatchers',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function deleteFuellbild(idImage) {
    const url = urlImagesRelease + '/s/v1/firma/delFiller/' + idImage;
    return axios({
        title: 'Löschen des Füllbilds',
        method: 'delete',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function changeFuellbild(idImage1, idImage2) {
    const url = urlImagesRelease + '/s/v1/firma/swapFiller/' + idImage1 + '/' + idImage2;
    return axios({
        title: 'Ändern des Füllbilds',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getBildersettings(idFirma, signal: AbortController['signal']) {
    if (checkUndefined(idFirma)) {
        idFirma = getIdFirma();
    }
    const url = urlImagesRelease + '/s/v2/firma/' + idFirma + '/settings';
    return axios<FirmaBilderSettings>({
        title: 'Abrufen der Bildeinstellungen',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
        signal: signal,
    });
}

export function putFahrzeugbild(idCar, nr, file, config) {
    const url = urlImagesRelease + '/s/v1/firma/' + getIdFirma() + '/user/' + getIdUser() + '/car/' + idCar + '/' + nr;
    return axios({
        title: 'Hochladen des Fahrzeugbilds',
        method: 'put',
        url: url,
        data: file,
        ...config,
    });
}

export function moveFahrzeugbild(idCar, array) {
    const url = urlImagesRelease + '/s/v2/firma/' + idCar + '/change';
    return axios({
        title: 'Verschieben des Fahrzeugbilds',
        method: 'post',
        url: url,
        data: array,
        headers: getAxiosHeader(),
    });
}

export function postEyecatcher(idCar) {
    const url = urlImagesRelease + '/s/v2/firma/' + getIdFirma() + '/' + idCar + '/genEyecatcherWeb';
    return axios({
        title: 'Eyecatcher neu generieren',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function updateEyecatcher(cars) {
    const url = urlImagesRelease + '/s/v2/firma/' + getIdFirma() + '/genEyecatcherWeb';
    return axios({
        title: 'Eyecatcher neu generieren',
        method: 'post',
        url: url,
        data: cars,
        headers: getAxiosHeader(),
    });
}

export function deleteFahrzeugbild(idCar: number, idImage: number) {
    return axios({
        title: 'Löschen des Fahrzeugbilds',
        method: 'delete',
        url: `${urlImagesRelease}/s/v2/firma/${getIdFirma()}/car/${idCar}/id/${idImage}`,
        headers: getAxiosHeader(),
    });
}

export function generateFinanzbild(idCar) {
    const url = urlImagesRelease + '/s/v1/finanzierung/' + getIdFirma() + '/car/' + idCar + '/user/' + getIdUser();
    return axios({
        title: 'Generieren des Finanzierungsbilds',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function put360Bild(idCar, file, config) {
    const url = urlImagesRelease + '/s/v1/firma/360/' + getIdFirma() + '/user/' + getIdUser() + '/car/' + idCar + '/0';
    return axios({
        title: 'Hochladen des 360-Grad-Innenbilds',
        method: 'put',
        url: url,
        data: file,
        ...config,
    });
}

export function put360AussenBild(idCar, file, config) {
    const url = urlImagesRelease + '/s/v1/firma/360a/' + getIdFirma() + '/user/' + getIdUser() + '/car/' + idCar + '/0';
    return axios({
        title: 'Hochladen des 360-Grad-Außenbilds',
        method: 'put',
        url: url,
        data: file,
        timeout: 0,
        ...config,
    });
}

export function putDamagebild(idCar, file, config) {
    const url = urlImagesRelease + '/s/v1/firma/damage/' + getIdFirma() + '/user/' + getIdUser() + '/car/' + idCar;
    return axios({
        title: 'Hochladen des Schadensbild',
        method: 'put',
        url: url,
        data: file,
        ...config,
    });
}

export function putOverlay(filename, file, config, idFirma?) {
    const newFilename = `${stringToSlug(filename.substring(0, filename.indexOf('.png')))}.png`;

    const url = urlImagesRelease + '/s/v2/firma/add/' + (idFirma || getIdFirma()) + '/user/' + getIdUser() + '/' + newFilename;
    return axios({
        title: 'Hochladen des Overlays',
        method: 'put',
        url: url,
        data: file,
        ...config,
    });
}

export function postRenderOverlay(idImage, idOverlay) {
    const url = urlImagesRelease + '/s/v2/firma/' + getIdFirma() + '/user/' + getIdUser() + '/image/' + idImage + '/overlay/' + idOverlay;
    return axios({
        title: 'Rendern des Overlays',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function deleteOverlay(idImage) {
    const url = urlImagesRelease + '/s/v2/firma/' + getIdFirma() + '/image/' + idImage + '/render';
    return axios({
        title: 'Löschen des Overlays',
        method: 'delete',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function setOverlayMultiple(idOverlay, cars) {
    const url = urlImagesRelease + '/s/v2/firma/' + getIdFirma() + '/user/' + getIdFirma() + '/overlay/' + idOverlay;
    return axios({
        title: 'Setzen der Overlays',
        method: 'post',
        url: url,
        data: cars,
        headers: getAxiosHeader(),
    });
}

export function deleteOverlayMultiple(cars) {
    const url = urlImagesRelease + '/s/v2/firma/' + getIdFirma() + '/user/' + getIdFirma() + '/overlay/-1';
    return axios({
        title: 'Löschen der Overlays',
        method: 'delete',
        url: url,
        data: cars,
        headers: getAxiosHeader(),
    });
}

export function deleteOverlayFromPool(idImage) {
    const url = urlImagesRelease + '/s/v2/firma/overlay/' + idImage + '/del';
    return axios({
        title: 'Löschen des Overlays',
        method: 'delete',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getLocations() {
    const url = urlSRelease + '/firma/location/' + getIdFirma() + '/' + getIdUser();
    return axios({
        title: 'Abrufen der Standorte',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getUsers() {
    const url = urlSRelease + '/v5/leads/mitarbeiter/redirect';
    return axios({
        title: 'Abrufen der Mitarbeiter',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function setVertretung(strForm) {
    const url = urlSRelease + '/firma/vertretung/' + getIdFirma() + '/' + getIdUser();
    return axios({
        title: 'Setzen der Vertretung',
        method: 'post',
        url: url,
        data: strForm,
        headers: getAxiosHeader(),
    });
}

export function deleteVertretung() {
    const url = urlSRelease + '/firma/vertretung/' + getIdFirma() + '/' + getIdUser();
    return axios({
        title: 'Beenden der Vertretung',
        method: 'delete',
        url: url,
        headers: getAxiosHeader(),
    });
}

export const loginUrl = LEAD ? '/session/loginj/' : '/sessionSpt/loginj/';
export const logoutUrl = LEAD ? '/session/logout/' : '/sessionSpt/logout/';
export const loginSessionUrl = LEAD ? '/session/loginData/' : '/sessionSpt/loginData/';

export function logout() {
    const url = urlSRelease + logoutUrl + getIdFirma() + '/' + getIdUser();
    return axios({
        title: 'Abmelden des Benutzers',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function login<T>(data: { u: string; p: string; v: string }) {
    const url = urlSRelease + loginUrl;
    return axios<T>({
        title: 'Anmelden des Benutzers',
        method: 'post',
        url: url,
        data: data,
    });
}

export function getLoginSession(token: Session['token']) {
    const url = urlSRelease + loginSessionUrl;
    return axios({
        title: 'Sessioncheck',
        method: 'get',
        url: url,
        headers: getAxiosHeader(token),
    });
}

export function loginOverUWE(enc) {
    const url = urlSRelease + '/session/login_enc';
    return axios({
        title: 'Anmelden via UWE-X',
        method: 'post',
        url: url,
        data: { enc: enc },
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
}

export function savePrint(strData) {
    const url = urlSRelease + '/firma/save_print/' + getIdFirma() + '/' + getIdUser();
    return axios({
        title: 'Speichern der Druckvorlagen-Einstellungen',
        method: 'post',
        url: url,
        data: strData,
        headers: getAxiosHeader(),
    });
}

export function saveEmailSettings(strData) {
    const url = urlSRelease + '/firma/save_settings_email/' + getIdFirma() + '/' + getIdUser();
    return axios({
        title: 'Speichern der Mail-Einstellungen',
        method: 'post',
        url: url,
        data: strData,
        headers: getAxiosHeader(),
    });
}

export function saveEskal(strData) {
    const url = urlSRelease + '/firma/save_settings_eskalation/' + getIdFirma() + '/' + getIdUser();
    return axios({
        title: 'Speichern der Eskalations-Einstellungen',
        method: 'post',
        url: url,
        data: strData,
        headers: getAxiosHeader(),
    });
}

export function saveResponder(strData) {
    const url = urlSRelease + '/firma/save_settings_autoresponder/' + getIdFirma() + '/' + getIdUser();
    return axios({
        title: 'Speichern der Autoresponder-Einstellungen',
        method: 'post',
        url: url,
        data: strData,
        headers: getAxiosHeader(),
    });
}

export function getSettings() {
    const url = urlSRelease + '/firma/settings/' + getIdFirma();
    return axios({
        title: 'Speichern der Druck- und Mail-Einstellungen',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function setAttrPrio(idFirma, idAttr, idPrio) {
    const url = urlSRelease + '/firma/setAttrPrio/' + idFirma + '/' + idAttr + '/' + idPrio;
    return axios({
        title: 'Speichern der Ausstattungspriorität',
        method: 'post',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function saveConfig(idFirma, idUser, idFirmaConfig, strData) {
    const url = urlSRelease + '/firma/setConfigGlobal/' + idFirma + '/' + idUser + '/' + idFirmaConfig;
    return axios({
        title: 'Speichern der Bewertungseinstellungen',
        method: 'post',
        url: url,
        data: strData,
        headers: getAxiosHeader(),
    });
}

export function getFirmaDaten(idFirma) {
    const url = urlSRelease + '/firma/data/' + getIdFirma() + '/' + idFirma;
    return axios<Firma>({
        title: 'Abrufen der Firmendaten',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getStatFirma(param, von, bis) {
    const von2 = isNaN(von) ? 0 : von;
    const bis2 = isNaN(bis) ? 0 : bis;
    const url = urlSRelease + '/v1/stat/firma/' + getIdFirma() + param + '/' + von2 + '/' + bis2;
    return axios({
        title: 'Abrufen der Mitarbeiter-Statistik',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getStatLeadStatus(idUser, idLoc, param, von, bis) {
    const von2 = isNaN(von) ? 0 : von;
    const bis2 = isNaN(bis) ? 0 : bis;
    const url = urlSRelease + '/v1/stat/firma/' + getIdFirma() + '/' + idUser + '/' + idLoc + param + '/' + von2 + '/' + bis2;
    return axios({
        title: 'Abrufen der Status-Statistik',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getPortalFirma(von, bis) {
    const von2 = isNaN(von) ? 0 : von;
    const bis2 = isNaN(bis) ? 0 : bis;
    const url = urlSRelease + '/v3/stat/firma/' + getIdFirma() + '/portal/' + von2 + '/' + bis2;
    return axios({
        title: 'Abrufen der Portal-Statistik',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getPortalGl(von, bis) {
    const von2 = isNaN(von) ? 0 : von;
    const bis2 = isNaN(bis) ? 0 : bis;
    const url = urlSRelease + '/v3/stat/global/portal/' + von2 + '/' + bis2;
    return axios({
        title: 'Abrufen der globalen Portal-Statistik',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getCompanyRating() {
    const url = urlExportRelease + '/mobile/sellerrating/overview';
    return axios({
        title: 'Abrufen der Firmenbewertung',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getCustomerRatings(filter) {
    const filters = [
        !checkUndefinedOrNull(filter.idUser) ? filter.idUser : -1,
        !checkUndefinedOrNull(filter.idLead) ? filter.idLead : -1,
        !checkUndefinedOrNull(filter.dateFrom) ? filter.dateFrom : -1,
        !checkUndefinedOrNull(filter.dateTo) ? filter.dateTo : -1,
    ];

    return axios({
        title: 'Abrufen der Kundenbewertungen',
        method: 'post',
        url: urlSRelease + '/v1/ratings/' + filters.join('/'),
        data: !checkUndefinedOrNull(filter.volltext) ? filter.volltext : undefined,
        headers: getAxiosHeader(),
    });
}

export function getCustomerRatingInvites() {
    return axios({
        title: 'Abrufen der Einladungen',
        method: 'get',
        url: urlSRelease + '/v1/ratings/invites',
        headers: getAxiosHeader(),
    });
}

export function editCustomerRating(data) {
    return axios({
        title: 'Bearbeiten der Bewertung',
        method: 'post',
        url: urlSRelease + '/v1/ratings/rating',
        data: data,
        headers: getAxiosHeader(),
    });
}

export function editCustomerRatingInvite(data) {
    return axios({
        title: 'Bearbeiten der Einladung',
        method: 'post',
        url: urlSRelease + '/v1/ratings/invite',
        data: data,
        headers: getAxiosHeader(),
    });
}

export function sendCustomerRatingInvite(data) {
    return axios({
        title: 'Senden der Einladung',
        method: 'post',
        url: urlSRelease + '/v1/ratings/sendMail/' + data,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function getAusstattungApi() {
    const url = urlServicesRelease + '/aus/api/attr';
    return axios({
        title: 'Abrufen aller Ausstattungen',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getAusstattungenNeu() {
    const url = urlServicesRelease + '/aus/v3/zuord/erkennung_neu';
    return axios({
        title: 'Abrufen aller Ausstattungen',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getAusstattungenFehler() {
    const url = urlServicesRelease + '/aus/v3/zuord/erkennung_old/0/100';
    return axios({
        title: 'Abrufen der Ausstattungsfehler',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getAusstattungenFehler4Attr(start, size, idAttr) {
    const url = urlServicesRelease + '/aus/v3/zuord/erkennung_old_attr/' + start + '/' + size + '/' + idAttr;
    return axios({
        title: 'Abrufen des Ausstattungsfehler',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getAusstattungenFehlerUmlate() {
    const url = urlServicesRelease + '/aus/v4/textfehler/fragezeichen';
    return axios({
        title: 'Abrufen der Umlautfehler',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getAusstattungenFehlerUmlateZuord(idAusstattung) {
    const url = urlServicesRelease + '/aus/v4/textfehler/' + idAusstattung;
    return axios({
        title: 'Abrufen des Umlautfehlers',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function addAttr(idText, idZuordnung) {
    const url = urlServicesRelease + '/aus/v4/zuord/' + idText + '/' + idZuordnung;
    return axios({
        title: 'Hinzufügen der Ausstattung',
        method: 'put',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function deleteAttrZuord(idText, idZuord) {
    const url = urlServicesRelease + '/aus/v4/zuord/' + idText + '/' + idZuord;
    return axios({
        title: 'Löschen der Zuordnung',
        method: 'delete',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function deleteAttr(idText) {
    const url = urlServicesRelease + '/aus/v4/text/' + idText;
    return axios({
        title: 'Löschen der Ausstattung',
        method: 'delete',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function addZuordToAttr(idTxtDel, data) {
    const url = urlServicesRelease + '/aus/v5/text/' + idTxtDel;
    return axios({
        title: 'Hinzufügen der Zuordnung',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function deleteAllZuordnungen(idText) {
    const url = urlServicesRelease + '/aus/v3/zuord/firma_' + getIdFirma() + '/' + 'user_' + getIdUser() + '/' + idText;
    return axios({
        title: 'Löschen aller Zuordnungen',
        method: 'delete',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function ignorZuordnung(idText, idVorschlag) {
    const url = urlServicesRelease + '/aus/v4/zuord/ignore/' + idText + '/' + idVorschlag;
    return axios({
        title: 'Ignorieren der Zuordnung',
        method: 'put',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function createVehicleGefuehrt(carDaten) {
    const url = urlServicesRelease + '/cars/v3/import/dat/ecode';
    return axios({
        title: 'Anlegen des Fahrzeugs',
        method: 'post',
        url: url,
        data: carDaten,
        headers: getAxiosHeader(),
    });
}

export function createVehicleManuell(data) {
    const url = urlServicesRelease + '/cars/v2/import/firma/' + getIdFirma() + '/user/' + getIdUser() + '/mymo';
    return axios({
        title: 'Anlegen des Fahrzeugs',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function vehiclePerHsnTsn(hsn, tsn) {
    const url = urlDataRelease + '/dat2017/v2/kba/' + hsn + '/' + tsn;
    return axios({
        title: 'Abrufen der Fahrzeugdaten über HSN/TSN',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getServiceAkte(idCar: number) {
    const url = `${urlServicesRelease}/akte/v2/car/${idCar}?ts=${new Date()?.valueOf()}`;
    return axios<CarAkte[]>({
        title: 'Abrufen der Fahrzeugakte',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function deleteAkte(idAkte) {
    const url = urlServicesRelease + '/akte/v2/document/' + idAkte;
    return axios<CarAkte[]>({
        title: 'Löschen der Datei',
        method: 'delete',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function downloadAkte(idAkte) {
    const url = urlServicesRelease + '/akte/v2/document/' + getToken() + '/' + idAkte;
    window.open(url);
    return axios({
        title: 'Herunterladen der Datei',
        method: 'get',
        url: url,
        headers: { ...getAxiosHeader(), 'Content-Type': 'application/pdf' },
    });
}

export function getAkteFull(idFirma, idVehicle) {
    const url = urlServicesRelease + '/akte/v2/document/' + idFirma + '/' + idVehicle;
    window.open(url);
    return axios({
        title: 'Herunterladen der kompletten Akte',
        method: 'get',
        url: url,
        headers: { ...getAxiosHeader(), 'Content-Type': 'application/zip' },
    });
}

export function uploadAkte(idCar: Car['id'], idTyp: number, file: File, name: string, config: UploadConfig) {
    const url = urlServicesRelease + '/akte/v2/car/' + idCar + '/' + idTyp + '/' + name;
    return axios<CarAkte[]>({
        title: 'Hochladen der Datei',
        method: 'put',
        url: url,
        data: file,
        headers: getAxiosHeaderPDF(),
        ...config,
    });
}

export function sendMailAkte(idCar, typ) {
    const url = urlServicesRelease + '/akte/v1/mail/car/' + idCar + '/' + typ;
    return axios({
        title: 'Senden der Mail',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getVehicleProto(idCar) {
    const url = urlServicesRelease + '/proto1/v1/car/' + idCar;
    return axios({
        title: 'Abrufen der Protokolle',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getFirmenProto(idFirma) {
    const url = urlServicesRelease + '/proto1/v1/firma/' + idFirma;
    return axios({
        title: 'Abrufen der Protokolle',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function downloadVehicleProto(idCar, name, asBlob: boolean) {
    const url = urlServicesRelease + '/proto1/v1/car/' + idCar + '/' + name;
    return axios({
        title: 'Herunterladen des Protokolls',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
        responseType: asBlob ? 'blob' : undefined,
    });
}

export function downloadFirmenProto(idFirma, name, asBlob: boolean, downloadProgressFunc: (progressEvent: NetworkProgress) => void) {
    const url = urlServicesRelease + '/proto1/v1/firma/' + idFirma + '/' + name;
    return axios({
        title: 'Herunterladen des Protokolls',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
        onDownloadProgress: (progressEvent) => networkHandler(progressEvent, downloadProgressFunc),
        responseType: asBlob ? 'blob' : undefined,
    });
}

export function getExportProto(idProto) {
    const url = urlServicesRelease + '/proto1/v1/export/' + idProto;
    return axios({
        title: 'Abrufen des Protokolls',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getImportProto(idProto) {
    const url = urlServicesRelease + '/proto1/v1/import/' + idProto;
    return axios({
        title: 'Abrufen des Protokolls',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getVehicles4DashboardAll() {
    const url = urlServicesRelease + '/cars/v1/dashboard/all/0';
    return axios({
        title: 'Abrufen der Fahrzeuge (Dashboard)',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getUsersDashboard() {
    const url = urlSRelease + '/v2/dashboard/mitarbeiter';
    return axios({
        title: 'Abrufen der Leadliste (Dashboard)',
        method: 'get',
        url: url,
        headers: { ...getAxiosHeader(), 'Content-Type': 'application/x-www-form-urlencoded' },
    });
}

export function getWeatherData() {
    const location = getFirma() ? getFirma().p : undefined;
    if (location) {
        const url = 'https://api.openweathermap.org/data/2.5/weather?q=' + location + ',de&appid=ef6e7d7936bcaa3d556f02da2b092964&lang=de&units=metric';
        return axios({
            title: 'Abrufen der Wetterdaten',
            method: 'get',
            url: url,
            noToast: true,
        });
    }
}

export function getSimilarCars(data) {
    const url = urlServicesRelease + '/cars/v4/similar/';
    return axios({
        title: 'Abrufen der ähnlichen Fahrzeuge',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function setExportEnabled(data) {
    const url = urlServicesRelease + '/cars/v4/protofeature/9/1';
    return axios({
        title: 'Freigeben des Exports',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function setExportDisable(data) {
    const url = urlServicesRelease + '/cars/v4/protofeature/9/0';
    return axios({
        title: 'Sperren des Exports',
        method: 'post',
        url: url,
        data: data,
        headers: getAxiosHeader(),
    });
}

export function getPreisfindung(idCar) {
    const url = urlServicesRelease + '/cars/v1/preisfindung/' + idCar;
    return axios({
        title: 'Abrufen der Preise',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getVinLastSend(idFirma) {
    const url = urlDataRelease + '/vw/firma/' + idFirma + '/p';
    return axios({
        title: 'Abrufen der letzten VIN-Sendung',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}

export function getVinLastGet(idFirma) {
    const url = urlDataRelease + '/vw/firma/' + idFirma + '/g';
    return axios({
        title: 'Abrufen der letzten VIN-Anforderung',
        method: 'get',
        url: url,
        headers: getAxiosHeader(),
    });
}
